(() => {
  const ROOT_SELECTOR = '[data-mr-product]';
  const ZOOM_SELECTOR = '[data-mr-product-zoom]';
  const IMAGE_SELECTOR = '.mr-product-gallery__image';
  const ZOOM_SCALE = 2.4;

  const clamp = (value, minimum, maximum) => {
    return Math.min(Math.max(value, minimum), maximum);
  };

  const resetZoom = (button) => {
    if (!button) return;

    const image = button.querySelector(IMAGE_SELECTOR);

    button.classList.remove('is-inline-zoomed');
    button.classList.remove('is-inline-dragging');
    button.dataset.mrZoomX = '0';
    button.dataset.mrZoomY = '0';
    button.dataset.mrZoomMoved = 'false';
    button.setAttribute('aria-pressed', 'false');

    if (image) {
      image.style.setProperty('--mr-inline-zoom-x', '0px');
      image.style.setProperty('--mr-inline-zoom-y', '0px');
    }
  };

  const closeExistingLightbox = (root) => {
    root
      .querySelectorAll('[data-mr-product-lightbox]')
      .forEach((dialog) => {
        if (dialog.open && typeof dialog.close === 'function') {
          dialog.close();
        }

        dialog.removeAttribute('open');
        dialog.hidden = true;
      });

    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
  };

  const initializeButton = (root, button) => {
    if (button.dataset.mrInlineZoomReady === 'true') {
      return;
    }

    const image = button.querySelector(IMAGE_SELECTOR);

    if (!image) return;

    button.dataset.mrInlineZoomReady = 'true';
    button.dataset.mrZoomX = '0';
    button.dataset.mrZoomY = '0';
    button.dataset.mrZoomMoved = 'false';
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('aria-label', 'Zoom product image');

    let pointerId = null;
    let startPointerX = 0;
    let startPointerY = 0;
    let startImageX = 0;
    let startImageY = 0;

    const setPosition = (x, y) => {
      const bounds = button.getBoundingClientRect();

      const maximumX =
        Math.max(0, (bounds.width * (ZOOM_SCALE - 1)) / 2);

      const maximumY =
        Math.max(0, (bounds.height * (ZOOM_SCALE - 1)) / 2);

      const nextX = clamp(x, -maximumX, maximumX);
      const nextY = clamp(y, -maximumY, maximumY);

      button.dataset.mrZoomX = String(nextX);
      button.dataset.mrZoomY = String(nextY);

      image.style.setProperty(
        '--mr-inline-zoom-x',
        `${nextX}px`
      );

      image.style.setProperty(
        '--mr-inline-zoom-y',
        `${nextY}px`
      );
    };

    button.addEventListener('pointerdown', (event) => {
      if (!button.classList.contains('is-inline-zoomed')) {
        return;
      }

      pointerId = event.pointerId;
      startPointerX = event.clientX;
      startPointerY = event.clientY;
      startImageX = Number(button.dataset.mrZoomX || 0);
      startImageY = Number(button.dataset.mrZoomY || 0);

      button.dataset.mrZoomMoved = 'false';
      button.classList.add('is-inline-dragging');

      button.setPointerCapture?.(pointerId);
      event.preventDefault();
      event.stopPropagation();
    });

    button.addEventListener('pointermove', (event) => {
      if (
        pointerId === null ||
        event.pointerId !== pointerId ||
        !button.classList.contains('is-inline-zoomed')
      ) {
        return;
      }

      const deltaX = event.clientX - startPointerX;
      const deltaY = event.clientY - startPointerY;

      if (
        Math.abs(deltaX) > 4 ||
        Math.abs(deltaY) > 4
      ) {
        button.dataset.mrZoomMoved = 'true';
      }

      setPosition(
        startImageX + deltaX,
        startImageY + deltaY
      );

      event.preventDefault();
      event.stopPropagation();
    });

    const stopDragging = (event) => {
      if (
        pointerId === null ||
        event.pointerId !== pointerId
      ) {
        return;
      }

      button.releasePointerCapture?.(pointerId);
      pointerId = null;
      button.classList.remove('is-inline-dragging');

      event.preventDefault();
      event.stopPropagation();
    };

    button.addEventListener('pointerup', stopDragging);
    button.addEventListener('pointercancel', stopDragging);

    button.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        resetZoom(button);
      }
    });
  };

  const initializeRoot = (root) => {
    closeExistingLightbox(root);

    const zoomEnabled =
      root.dataset.mrInlineZoomEnabled === 'true';

    if (!zoomEnabled) {
      root
        .querySelectorAll(ZOOM_SELECTOR)
        .forEach((button) => {
          resetZoom(button);
          button.classList.add('is-inline-zoom-disabled');
          button.setAttribute('aria-pressed', 'false');
          button.setAttribute('aria-label', 'Product image');
        });

      return;
    }

    root
      .querySelectorAll(ZOOM_SELECTOR)
      .forEach((button) => {
        button.classList.remove('is-inline-zoom-disabled');
        initializeButton(root, button);
      });

    /*
     * Capture-Listener:
     * verhindert, dass die alte mr-product.js-Lightbox
     * den Klick später noch verarbeitet.
     */
    root.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest(ZOOM_SELECTOR);

        if (!button || !root.contains(button)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        closeExistingLightbox(root);

        if (button.dataset.mrZoomMoved === 'true') {
          button.dataset.mrZoomMoved = 'false';
          return;
        }

        const shouldZoom =
          !button.classList.contains('is-inline-zoomed');

        root
          .querySelectorAll(ZOOM_SELECTOR)
          .forEach((otherButton) => {
            if (otherButton !== button) {
              resetZoom(otherButton);
            }
          });

        if (shouldZoom) {
          button.classList.add('is-inline-zoomed');
          button.setAttribute('aria-pressed', 'true');

          const image = button.querySelector(IMAGE_SELECTOR);

          if (image) {
            image.style.setProperty(
              '--mr-inline-zoom-x',
              '0px'
            );

            image.style.setProperty(
              '--mr-inline-zoom-y',
              '0px'
            );
          }
        } else {
          resetZoom(button);
        }
      },
      true
    );

    root.addEventListener(
      'click',
      (event) => {
        const thumbnail = event.target.closest(
          '[data-mr-product-thumbnail]'
        );

        if (!thumbnail) return;

        root
          .querySelectorAll(ZOOM_SELECTOR)
          .forEach(resetZoom);
      },
      true
    );
  };

  const initializeAll = () => {
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initializeRoot);
  };

  document.addEventListener(
    'DOMContentLoaded',
    initializeAll
  );

  document.addEventListener(
    'shopify:section:load',
    initializeAll
  );

  initializeAll();
})();
